import type { JobData } from '../types/optimization';

/**
 * Analyzes resume content density and expansion potential
 * @param resumeText The resume text to analyze
 * @returns Object containing density score and expansion potential
 */
export function analyzeResumeDensity(resumeText: string): { density: number; expansionPotential: number } {
  const sections = ['experience', 'education', 'skills', 'projects', 'summary', 'objective'];
  let sectionCount = 0;
  let contentDensity = 0;
  
  // Count sections and calculate density
  sections.forEach(section => {
    const regex = new RegExp(`(#\\s*${section}|${section}\\s*:)`, 'gi');
    if (regex.test(resumeText)) {
      sectionCount++;
      
      // Extract section content
      const sectionRegex = new RegExp(`(#\\s*${section}|${section}\\s*:)[^#]*`, 'gi');
      const match = resumeText.match(sectionRegex);
      
      if (match) {
        match.forEach(content => {
          const lines = content.split('\n').filter(line => line.trim().length > 0).length;
          const words = content.split(/\s+/).filter(word => word.trim().length > 0).length;
          contentDensity += words / Math.max(1, lines);
        });
      }
    }
  });
  
  const averageDensity = contentDensity / Math.max(1, sectionCount);
  return {
    density: averageDensity,
    expansionPotential: sectionCount < 4 ? 0.8 : 0.4
  };
}

/**
 * Checks if resume contains critical sections
 * @param resumeText The resume text to check
 * @returns Boolean indicating if resume has critical sections
 */
export function isResumeComplete(resumeText: string): boolean {
  const requiredSections = ['experience', 'education', 'skills'];
  const lowerResume = resumeText.toLowerCase();
  return requiredSections.some(section => lowerResume.includes(section));
}

/**
 * Extracts contact details from resume text
 * @param resumeText The resume text to extract from
 * @returns Object containing extracted contact details
 */
export function extractContactDetails(resumeText: string): { 
  name: string; 
  email: string; 
  phone: string; 
  linkedin: string; 
  portfolio: string 
} {
  const patterns = {
    name: /(\b[A-Z][a-z]+(?:\s+[A-Z][a-z]+)+\b)/,
    email: /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/,
    phone: /(\+?\d{1,2}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/,
    linkedin: /(linkedin\.com\/in\/[a-zA-Z0-9-]+)/i,
    portfolio: /(https?:\/\/[^\s]+\.[a-zA-Z]{2,})/i
  };

  return {
    name: (resumeText.match(patterns.name) || [])[0] || '',
    email: (resumeText.match(patterns.email) || [])[0] || '',
    phone: (resumeText.match(patterns.phone) || [])[0] || '',
    linkedin: (resumeText.match(patterns.linkedin) || [])[0] || '',
    portfolio: (resumeText.match(patterns.portfolio) || [])[0] || ''
  };
}

/**
 * Detects experience level from resume text and job data
 * @param resumeText The resume text to analyze
 * @param jobData Optional job data for additional context
 * @returns String indicating experience level (Junior, Mid-Level, Senior)
 */
export function detectExperienceLevel(resumeText: string, jobData?: JobData): string {
  try {
    // 1. Extract years of experience
    const yearsPattern = /(\d+)\+?\s*(?:years?|yrs?|years of experience)/gi;
    const yearsMatches = [...resumeText.matchAll(yearsPattern)];
    const yearsExp = yearsMatches.length
      ? Math.max(...yearsMatches.map(m => parseInt(m[1] || '0')))
      : 0;

    // 2. Determine level from title if available
    const title = jobData?.title?.toLowerCase() || '';
    const seniorTitles = ['director', 'vp', 'vice president', 'chief', 'cto', 'head', 'principal', 'sr\\.', 'senior'];
    const midTitles = ['manager', 'lead', 'mid', 'intermediate'];
    const juniorTitles = ['junior', 'jr\\.', 'entry', 'associate'];

    // 3. Title-based detection
    if (title) {
      const seniorRegex = new RegExp(seniorTitles.join('|'), 'i');
      if (seniorRegex.test(title)) return 'Senior';

      const midRegex = new RegExp(midTitles.join('|'), 'i');
      if (midRegex.test(title)) return 'Mid-Level';

      const juniorRegex = new RegExp(juniorTitles.join('|'), 'i');
      if (juniorRegex.test(title)) return 'Junior';
    }

    // 4. Fallback to years of experience
    if (yearsExp >= 5) return 'Senior';
    if (yearsExp >= 2) return 'Mid-Level';
    return 'Junior';

  } catch (error) {
    console.error('Experience level detection failed:', error);
    return 'Mid-Level'; // Graceful fallback
  }
}

/**
 * Extracts key improvements between original and optimized text
 * @param originalText Original resume text
 * @param optimizedText Optimized resume text
 * @returns Array of improvement descriptions
 */
export function extractKeyImprovements(originalText: string, optimizedText: string): string[] {
  const improvements: string[] = [];
  
  // 1. Word count analysis
  const origWordCount = originalText.trim().split(/\s+/).length || 1;
  const optWordCount = optimizedText.split(/\s+/).length;
  const changePercent = Math.round(((optWordCount - origWordCount) / origWordCount) * 100);

  if (changePercent !== 0) {
    improvements.push(
      `Content ${changePercent > 0 ? 'expanded' : 'condensed'} by ${Math.abs(changePercent)}%`
    );
  }

  // 2. Section analysis
  const analyzeSection = (section: string) => {
    const sectionRegex = new RegExp(`#\\s*${section}[\\s\\S]*?(?=#|$)`, 'i');
    const origSection = originalText.match(sectionRegex)?.[0] || '';
    const optSection = optimizedText.match(sectionRegex)?.[0] || '';
    
    if (origSection && optSection) {
      const lineDiff = optSection.split('\n').length - origSection.split('\n').length;
      if (Math.abs(lineDiff) > 2) {
        return `${section} section ${lineDiff > 0 ? 'expanded' : 'condensed'} by ${Math.abs(lineDiff)} points`;
      }
    }
    return null;
  };

  ['experience', 'education', 'skills', 'professional summary'].forEach(section => {
    const change = analyzeSection(section);
    if (change) improvements.push(change);
  });

  // 3. Specific quality checks
  if ((optimizedText.match(/\d+%/g) || []).length > 2) {
    improvements.push('Added quantifiable metrics');
  }
  
  if ((optimizedText.match(/\b(achieved|improved|increased|led|managed|developed)\b/gi) || []).length > 
     (originalText.match(/\b(achieved|improved|increased|led|managed|developed)\b/gi) || []).length + 3) {
    improvements.push('Enhanced with action verbs');
  }

  return improvements.slice(0, 4); // Return top 4 improvements
}

/**
 * Identifies skills gaps between resume and job requirements
 * @param resumeText Resume text to analyze
 * @param requiredSkills Array of required skills from job
 * @returns Array of missing skills
 */
export function identifySkillsGaps(resumeText: string, requiredSkills: string[] = []): string[] {
  try {
    console.log('DEBUG: identifySkillsGaps called with:', {
      resumeTextLength: resumeText?.length || 0,
      resumeTextPreview: resumeText?.substring(0, 100) || '',
      requiredSkillsCount: requiredSkills?.length || 0,
      requiredSkills: requiredSkills
    });
    
    if (!requiredSkills || requiredSkills.length === 0) {
      console.log('DEBUG: identifySkillsGaps - No required skills provided, returning empty array');
      return [];
    }

    const skillsGaps: string[] = [];
    const resumeLower = resumeText.toLowerCase();
    
    console.log('DEBUG: identifySkillsGaps - Processing skills for gaps...');
    
    requiredSkills.forEach(skill => {
      if (!skill || skill.trim().length === 0) return;
      
      const skillLower = skill.toLowerCase().trim();
      console.log(`DEBUG: identifySkillsGaps - Checking skill: "${skill}" (${skillLower})`);
      
      // Check for exact match
      if (resumeLower.includes(skillLower)) {
        console.log(`DEBUG: identifySkillsGaps - Found exact match for: ${skill}`);
        return;
      }
      
      // Check for variations and synonyms
      const skillVariations = generateSkillVariations(skillLower);
      console.log(`DEBUG: identifySkillsGaps - Generated variations for "${skill}":`, skillVariations);
      
      const hasVariation = skillVariations.some(variation => 
        new RegExp(`\\b${variation.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(resumeText)
      );
      
      if (!hasVariation) {
        console.log(`DEBUG: identifySkillsGaps - Adding skill gap: ${skill}`);
        skillsGaps.push(skill);
      } else {
        console.log(`DEBUG: identifySkillsGaps - Found variation match for: ${skill}`);
      }
    });
    
    console.log('DEBUG: identifySkillsGaps - Final skills gaps:', {
      skillsGapsCount: skillsGaps.length,
      skillsGaps: skillsGaps.slice(0, 8)
    });
    
    return skillsGaps.slice(0, 8); // Limit to 8 most important gaps
  } catch (error) {
    console.error('Error identifying skills gaps:', error);
    console.log('DEBUG: identifySkillsGaps - Error occurred, returning empty array');
    return [];
  }
}

/**
 * Generate variations of a skill for better matching
 * @param skill The skill to generate variations for
 * @returns Array of skill variations
 */
function generateSkillVariations(skill: string): string[] {
  const variations = [skill];
  
  // Common technology variations
  const techVariations: Record<string, string[]> = {
    'javascript': ['js', 'ecmascript', 'es6', 'es2015'],
    'typescript': ['ts'],
    'python': ['py'],
    'c#': ['csharp', 'c sharp'],
    'node.js': ['nodejs', 'node'],
    'react.js': ['react', 'reactjs'],
    'vue.js': ['vue', 'vuejs'],
    'angular.js': ['angular', 'angularjs'],
    'machine learning': ['ml', 'artificial intelligence', 'ai'],
    'artificial intelligence': ['ai', 'machine learning', 'ml'],
    'user experience': ['ux', 'user interface', 'ui'],
    'user interface': ['ui', 'user experience', 'ux'],
    'search engine optimization': ['seo'],
    'customer relationship management': ['crm'],
    'enterprise resource planning': ['erp']
  };
  
  // Add known variations
  if (techVariations[skill]) {
    variations.push(...techVariations[skill]);
  }
  
  // Add common patterns
  if (skill.includes('.')) {
    variations.push(skill.replace(/\./g, ''));
  }
  
  if (skill.includes(' ')) {
    variations.push(skill.replace(/\s+/g, ''));
  }
  
  return variations;
}

/**
 * Extracts AI suggestions from optimized text
 * @param text Optimized text to analyze
 * @returns Array of AI suggestions
 */
export function extractAISuggestions(text: string): string[] {
  const suggestions: string[] = [];
  
  // Check for quantifiable metrics
  if (!text.match(/\d+%/g)) {
    suggestions.push("Add quantifiable metrics");
  }
  
  // Check for action verbs
  if (!text.match(/\b(achieved|improved|increased)\b/gi)) {
    suggestions.push("Add more action-oriented language");
  }
  
  // Check for technical skills section
  if (!text.match(/\b(technical skills|technologies|programming languages)\b/gi)) {
    suggestions.push("Add a dedicated technical skills section");
  }
  
  // Check for education details
  if (text.match(/\beducation\b/gi) && !text.match(/\b(gpa|cum laude|honors|distinction)\b/gi)) {
    suggestions.push("Consider adding academic achievements");
  }
  
  return suggestions;
}
